'use strict';
/**
 * Accordion controller.
 */
class Accordion {
    constructor(domNode) {
        this.rootEl = domNode;
        this.buttonEl = this.rootEl.querySelector('button[aria-expanded]');

        if (!this.buttonEl) return;

        const controlsId = this.buttonEl.getAttribute('aria-controls');
        this.contentEl = document.getElementById(controlsId);
        this.panelInner = this.contentEl ? this.contentEl.firstElementChild : null;

        this.isOpen = this.buttonEl.getAttribute('aria-expanded') === 'true';
        this.accordions = [];
        this.transitionEndHandler = null;

        // Only a nested accordion sits inside another accordion's panel. Those
        // panels have a measured max-height, so they have to be released when
        // this one opens or the nested content is clipped.
        const parent = this.rootEl.parentElement;
        this.ancestorPanels = [];
        let ancestorPanel = parent ? parent.closest('.accordion-panel') : null;

        while (ancestorPanel) {
            this.ancestorPanels.push(ancestorPanel);
            ancestorPanel = ancestorPanel.parentElement
                ? ancestorPanel.parentElement.closest('.accordion-panel')
                : null;
        }

        this.buttonEl.addEventListener('click', () => this.onButtonClick());

        if (this.isOpen && this.contentEl) {
            this.contentEl.hidden = false;
            this.setPanelHeight();
            this.contentEl.classList.add('is-open');
        }
    }

    onButtonClick() {
        this.toggle(!this.isOpen);
    }

    toggle(open) {
        if (open === this.isOpen) return;

        this.isOpen = open;
        this.buttonEl.setAttribute('aria-expanded', `${open}`);

        if (open) {
            this.accordions.forEach(accordion => {
                if (accordion !== this) {
                    accordion.close();
                }
            });

            this.releaseAncestorPanels();

            if (this.contentEl) {
                if (this.transitionEndHandler) {
                    this.contentEl.removeEventListener('transitionend', this.transitionEndHandler);
                    this.transitionEndHandler = null;
                }
                this.contentEl.hidden = false;
                this.setPanelHeight();
                requestAnimationFrame(() => {
                    this.contentEl.classList.add('is-open');
                });
            }

        } else {
            this.closePanel();
        }
    }

    setPanelHeight() {
        if (!this.contentEl || !this.panelInner) return;
        const panelStyles = window.getComputedStyle(this.contentEl);
        const panelPaddingTop = parseFloat(panelStyles.paddingTop) || 0;
        const panelPaddingBottom = parseFloat(panelStyles.paddingBottom) || 0;
        const height = this.panelInner.scrollHeight + panelPaddingTop + panelPaddingBottom;
        this.contentEl.style.setProperty('--panel-height', `${height}px`);
    }

    /**
     * Lets every open ancestor panel grow freely so nested content is never cut off.
     * A no-op for top-level accordions, which have no ancestor panel.
     */
    releaseAncestorPanels() {
        this.ancestorPanels.forEach(panel => {
            if (panel.classList.contains('is-open')) {
                panel.classList.add('is-unbounded');
            }
        });
    }

    closePanel() {
        if (!this.contentEl) return;

        if (this.contentEl.classList.contains('is-unbounded')) {
            // Re-anchor to a measured height so the collapse still animates.
            this.setPanelHeight();
            this.contentEl.classList.remove('is-unbounded');
            void this.contentEl.offsetHeight;
        }

        this.contentEl.classList.remove('is-open');

        if (this.transitionEndHandler) {
            this.contentEl.removeEventListener('transitionend', this.transitionEndHandler);
        }

        this.transitionEndHandler = (event) => {
            // Nested panels transition the same property, so ignore bubbled events.
            if (event.target !== this.contentEl) return;
            if (event.propertyName !== 'max-height') return;
            this.contentEl.hidden = true;
            if (this.transitionEndHandler) {
                this.contentEl.removeEventListener('transitionend', this.transitionEndHandler);
                this.transitionEndHandler = null;
            }
        };

        this.contentEl.addEventListener('transitionend', this.transitionEndHandler);
    }

    open() {
        this.toggle(true);
    }

    close() {
        this.toggle(false);
    }

    destroy() {
    }
}

/**
 * Wires a list of heading elements into one single-open accordion group.
 */
const initAccordionGroup = (headings) => {
    const instances = [];

    headings.forEach(heading => {
        const instance = new Accordion(heading);

        if (instance.buttonEl) {
            instances.push(instance);
        }
    });

    instances.forEach(accordion => {
        accordion.accordions = instances;
    });
};

/**
 * Wires up accordions.
 */
const accordionAutomaticInit = () => {
    const accordionGroups = document.querySelectorAll('.accordions__outer');

    accordionGroups.forEach((group) => {
        const headings = Array.from(group.querySelectorAll('.accordion h3'))
            .filter(heading => !heading.closest('.accordion-nested'));

        initAccordionGroup(headings);
    });

    // Nested groups get their own instance list so opening a child closes only
    // its nested siblings, leaving the parent and top-level items untouched.
    const nestedGroups = document.querySelectorAll('.accordion-nested');

    nestedGroups.forEach((group) => {
        initAccordionGroup(Array.from(group.querySelectorAll('.accordion-nested__heading')));
    });
}

export default accordionAutomaticInit;
